import { ResponsiveSettings } from './ResponsiveSettings.js';
import {
  AUDIO_FILE_ACCEPT,
  COVER_FILE_ACCEPT,
  NEUTRAL_TUNING,
  TUNING_ROWS,
  applyFeedback,
  clampTuning,
  formatMib,
  type AutoParameters,
  type ChartTuning,
  type CreationPhase,
  type EventSources,
  type GeneratorMode,
  CHUNK_SIZES,
  EMPTY_DIRECTION,
  GEOMETRY_NAMES,
  PATTERN_NAMES,
  SOURCE_NAMES,
  directsAnything,
  emptySection,
  tidySections,
  sectionsFromAnalysis,
  type ChartDirection,
  type ChunkSize,
  type DetectedSection,
  type SectionAnalysis,
  type SemanticAnalysis,
  type DirectionSection,
} from '../creation/index.js';

/** Containers a `<video>` can decode and a Song Pack can carry. */
export const VIDEO_FILE_ACCEPT = '.mp4,.webm,video/mp4,video/webm';

/**
 * CREATE SONG.
 *
 * ```
 * CREATE SONG
 *   [ SELECT AUDIO ]
 *   TITLE   [               ]
 *   AUTHOR  [ Jinn Project  ]
 *   [ COVER IMAGE ]
 *   [ GENERATE ]
 *   STATUS
 *   ----------------
 *   Analyzing...
 *   Detecting BPM...
 *   Generating Easy...
 * ```
 *
 * The status list is a *log*, not a spinner, and that is the one design decision
 * in this file worth defending. Chart generation on a four-minute song takes long
 * enough that a single animated line tells the player nothing about whether it is
 * working, stuck, or has quietly failed. A growing list of stages — each one from
 * the engine's own progress reporting, not invented here — makes the difference
 * visible, and when it does fail the last line before the error says where.
 *
 * The screen owns no policy. It cannot generate, validate or import anything; it
 * collects four fields and calls back. Everything it displays about progress
 * arrives from `SongCreationService`, which gets it from the engine.
 */

const STYLE_ID = 'mandalizm-create-song-style';

/** Rows the advanced panel renders, and the arithmetic behind the three answers. */

const CSS = `
.mandalizm-create {
  position: fixed; inset: 0; z-index: 70; display: grid; place-items: center;
  background: radial-gradient(circle at 50% 40%, rgba(2,20,26,0.9), rgba(0,2,8,0.97));
  font: 13px/1.7 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: #bff4ee;
}
.mandalizm-create[hidden] { display: none; }
.mandalizm-create__inner {
  width: min(620px, 88vw); max-height: 88vh; overflow-y: auto; padding: 4px 2px;
}
.mandalizm-create__title {
  font-size: 22px; letter-spacing: 0.22em; color: #eafeff; margin-bottom: 20px;
  text-shadow: 0 0 22px rgba(0, 220, 200, 0.35);
}
.mandalizm-create__field { margin-bottom: 14px; }
.mandalizm-create__label {
  display: block; font-size: 10px; letter-spacing: 0.22em;
  color: rgba(140, 205, 220, 0.62); margin-bottom: 5px;
}
.mandalizm-create__row { display: flex; gap: 10px; align-items: center; }
.mandalizm-create__pick {
  appearance: none; cursor: pointer; font: inherit; font-size: 11px; letter-spacing: 0.16em;
  padding: 10px 18px; color: #b6e8e4; white-space: nowrap;
  background: rgba(4, 42, 50, 0.85); border: 1px solid rgba(90, 240, 220, 0.3);
  border-radius: 6px; transition: background 140ms ease, color 140ms ease;
}
.mandalizm-create__pick:hover:not(:disabled) { background: rgba(6, 76, 86, 0.95); color: #eafeff; }
.mandalizm-create__pick:disabled { opacity: 0.4; cursor: not-allowed; }
.mandalizm-create__chosen {
  flex: 1 1 auto; min-width: 0; color: #7fe9dc; font-size: 12px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.mandalizm-create__input {
  width: 100%; box-sizing: border-box; font: inherit; color: #eafeff;
  padding: 9px 11px; background: rgba(2, 18, 24, 0.9);
  border: 1px solid rgba(90, 240, 220, 0.24); border-radius: 6px;
}
.mandalizm-create__input:focus { outline: none; border-color: rgba(120, 250, 232, 0.7); }
.mandalizm-create__actions { display: flex; gap: 10px; margin: 20px 0 16px; }
.mandalizm-create__action {
  appearance: none; cursor: pointer; font: inherit; letter-spacing: 0.18em;
  padding: 12px 26px; color: #eafeff;
  background: rgba(4, 60, 66, 0.85); border: 1px solid rgba(90, 240, 220, 0.5);
  border-radius: 6px; transition: background 160ms ease;
}
.mandalizm-create__action:hover:not(:disabled) { background: rgba(6, 92, 100, 0.95); }
.mandalizm-create__action:disabled { opacity: 0.4; cursor: not-allowed; }
.mandalizm-create__action--quiet {
  background: rgba(3, 26, 34, 0.7); border-color: rgba(90, 240, 220, 0.2); color: #9fd8dd;
}
.mandalizm-create__bar {
  height: 3px; background: rgba(3, 30, 38, 0.9); border-radius: 2px; overflow: hidden;
  margin-bottom: 10px;
}
.mandalizm-create__fill {
  height: 100%; width: 0%; background: rgba(32, 232, 208, 0.9);
  transition: width 220ms ease;
}
.mandalizm-create__status {
  border-top: 1px solid rgba(90, 240, 220, 0.16); padding-top: 10px;
  max-height: 30vh; overflow-y: auto;
}
.mandalizm-create__line { color: rgba(150, 215, 225, 0.8); font-size: 12px; }
.mandalizm-create__line--active { color: #eafeff; }
.mandalizm-create__line--note { color: rgba(130, 190, 205, 0.62); font-size: 11px; }
.mandalizm-create__modes { display: flex; gap: 8px; flex-wrap: wrap; margin: 2px 0 10px; }
.mandalizm-create__mode {
  flex: 1 1 0; min-width: 88px; padding: 9px 10px; cursor: pointer;
  background: rgba(4,26,32,0.7); color: #7fd8cf; border: 1px solid #1d4f56;
  font: 600 11px/1 inherit; letter-spacing: 0.14em;
}
.mandalizm-create__mode:hover { border-color: #33cfc0; }
.mandalizm-create__mode--active { background: rgba(51,207,192,0.16); color: #d7fffa; border-color: #33cfc0; }
.mandalizm-create__analysis { display: grid; gap: 2px; margin-top: 4px; }
.mandalizm-create__readout {
  display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center;
  padding: 6px 8px; background: rgba(3,14,18,0.55); border-left: 2px solid #1d4f56; color: #8fb6b8;
}
.mandalizm-create__readout--measured { border-left-color: #33cfc0; color: #cdfaf3; }
.mandalizm-create__readout-value { font-variant-numeric: tabular-nums; color: #eafffb; }
.mandalizm-create__tag {
  font: 600 9px/1 inherit; letter-spacing: 0.12em; padding: 3px 6px;
  border: 1px solid #26424a; color: #6f9198; background: rgba(6,18,22,0.6);
}
.mandalizm-create__tag--adjusted { border-color: #b58a2f; color: #ffca7a; background: rgba(255,202,122,0.12); }
.mandalizm-create__tag--measured { border-color: #2f8f86; color: #6ee7d8; background: rgba(51,207,192,0.12); }
.mandalizm-create__legend { display: flex; gap: 8px; margin-top: 8px; }
.mandalizm-create__advanced { margin: 10px 0 4px; border: 1px solid #1d4f56; padding: 8px 10px; }
.mandalizm-create__advanced[hidden] { display: none; }
.mandalizm-create__advanced summary { cursor: pointer; letter-spacing: 0.14em; font-weight: 600; }
.mandalizm-create__slider {
  display: grid; grid-template-columns: 132px 1fr 78px auto; gap: 8px; align-items: center; margin-top: 10px;
}
.mandalizm-create__slider input { width: 100%; accent-color: #33cfc0; }
.mandalizm-create__slider-value { font-variant-numeric: tabular-nums; text-align: right; color: #eafffb; }
.mandalizm-create__reset {
  cursor: pointer; padding: 5px 8px; font: 600 9px/1 inherit; letter-spacing: 0.1em;
  background: transparent; color: #7fd8cf; border: 1px solid #1d4f56;
}
.mandalizm-create__reset:hover { border-color: #33cfc0; }
.mandalizm-create__group { border-top: 1px solid #16323a; margin-top: 12px; padding-top: 10px; }
.mandalizm-create__timeline {
  position: relative; height: 26px; margin: 8px 0; border: 1px solid #1d4f56;
  background: repeating-linear-gradient(90deg, rgba(51,207,192,0.08) 0 1px, transparent 1px 10%);
}
.mandalizm-create__timeline-block {
  position: absolute; top: 0; bottom: 0; background: rgba(51,207,192,0.28);
  border-left: 1px solid #33cfc0; border-right: 1px solid #33cfc0;
}
.mandalizm-create__sections { display: grid; gap: 6px; }
.mandalizm-create__section {
  display: grid; grid-template-columns: 1fr 76px 76px 1fr 1fr auto; gap: 6px; align-items: center;
}
.mandalizm-create__number, .mandalizm-create__select {
  background: rgba(4,20,26,0.8); color: #cdfaf3; border: 1px solid #1d4f56;
  font: 11px/1.4 inherit; padding: 5px 6px;
}
.mandalizm-create__detected { display: grid; gap: 4px; margin-top: 4px; }
.mandalizm-create__detected-row {
  display: grid; grid-template-columns: auto 96px 1fr 1fr auto; gap: 8px; align-items: center;
  padding: 5px 7px; background: rgba(3,14,18,0.55); border-left: 2px solid #33cfc0;
}
.mandalizm-create__detected-detail { color: #8fb6b8; font-size: 11px; }
.mandalizm-create__line--error { color: #ff86a6; white-space: pre-wrap; }
.mandalizm-create__line--done { color: #7ef5cf; }
`;

export interface CreateSongFields {
  readonly audio: File | null;
  readonly cover: File | null;
  /**
   * A music video to bundle with the song.
   *
   * Never reaches the chart engine. Python analyses audio; sending it a 200 MB
   * video would be 200 MB of upload for a file it would ignore. It is attached
   * to the Song Pack afterwards, by the library.
   */
  readonly video: File | null;
  readonly title: string;
  readonly artist: string;
  /** AUTO unless the player switched the toggle. */
  readonly mode: GeneratorMode;
  /**
   * The dial positions as they stand.
   *
   * Always present, even in AUTO, where every one of them sits at the
   * engine default — which is what makes switching to CUSTOM and changing
   * nothing produce the same chart rather than a different one.
   */
  readonly tuning: ChartTuning;
  /**
   * What the player directed: shapes, traversals, source bias, sections.
   *
   * Always present and usually empty. An empty direction is not a request
   * to change nothing — it is the absence of a request, and the client
   * drops it rather than sending neutral values the engine would have to
   * interpret.
   */
  readonly direction: ChartDirection;
  /** The direction the previous generation used, if there was one. */
  readonly lastDirection: ChartDirection | null;
  /**
   * Whether DIRECTED AUTO may read the song's structure.
   *
   * True unless the player turned it off, in which case the engine directs
   * from intensity alone — the behaviour that existed before it could read
   * anything, kept reachable rather than replaced.
   */
  readonly semantic: boolean;
}

export interface CreateSongScreenOptions {
  readonly onTitle?: () => void;
  readonly parent?: HTMLElement;
  readonly defaultArtist?: string;
  /** Called on GENERATE. Rejections are shown in the status list. */
  readonly onGenerate: (fields: CreateSongFields) => void | Promise<void>;
  /**
   * Called by REGENERATE, after a correction.
   *
   * Separate from `onGenerate` only so the host can tell a first attempt
   * from a correction of one; the fields carry everything either needs.
   */
  readonly onRegenerate?: (fields: CreateSongFields) => void | Promise<void>;
  /**
   * Called by REGENERATE SECTION.
   *
   * The index is into the sections the engine detected, which is what the
   * row the player pressed was describing.
   */
  readonly onRegenerateSection?: (
    index: number,
    fields: CreateSongFields,
  ) => void | Promise<void>;
  readonly onCancel: () => void;
  readonly onClose: () => void;
}

export class CreateSongScreen {
  private readonly advancedSettings: ResponsiveSettings;
  readonly element: HTMLDivElement;

  private readonly audioInput: HTMLInputElement;
  private readonly coverInput: HTMLInputElement;
  private readonly videoInput: HTMLInputElement;
  private readonly audioChosen: HTMLSpanElement;
  private readonly coverChosen: HTMLSpanElement;
  private readonly videoChosen: HTMLSpanElement;
  private readonly titleInput: HTMLInputElement;
  private readonly artistInput: HTMLInputElement;
  private readonly generateButton: HTMLButtonElement;
  private readonly cancelButton: HTMLButtonElement;
  private readonly closeButton: HTMLButtonElement;
  private readonly pickButtons: HTMLButtonElement[] = [];
  private readonly status: HTMLDivElement;
  private readonly fill: HTMLDivElement;
  private readonly options: CreateSongScreenOptions;
  private readonly modeButtons: readonly HTMLButtonElement[];
  private readonly analysis: HTMLDivElement;
  private readonly analysisSection: HTMLDivElement;
  private readonly advanced: HTMLDetailsElement;
  private readonly feedbackSection: HTMLDivElement;
  private readonly sliders = new Map<keyof ChartTuning, HTMLInputElement>();
  private readonly sliderValues = new Map<keyof ChartTuning, HTMLSpanElement>();
  private mode: GeneratorMode = 'AUTO';
  private tuning: ChartTuning = { ...NEUTRAL_TUNING };
  private auto: AutoParameters | null = null;
  /** The last analysis shown, so the adopt button has something to copy. */
  private detected: SectionAnalysis | null = null;
  /** The engine's inferred reading, or null when it did not produce one. */
  private semantics: SemanticAnalysis | null = null;
  private lastSent: ChartDirection | null = null;
  private readonly detectedList: HTMLDivElement;
  private readonly detectedSection: HTMLDivElement;
  private direction: ChartDirection = EMPTY_DIRECTION;
  private songSeconds = 0;
  private readonly directionPanel: HTMLDetailsElement;
  private readonly sectionList: HTMLDivElement;
  private readonly sectionBar: HTMLDivElement;
  private readonly weightInputs = new Map<string, HTMLInputElement>();
  private readonly chunkButtons: readonly HTMLButtonElement[];
  private readonly structureToggle: HTMLInputElement;
  /** Keyed by stage so a repeated poll updates its line instead of adding one. */
  private readonly stageLines = new Map<string, HTMLDivElement>();

  constructor(options: CreateSongScreenOptions) {
    injectStyle();
    this.options = options;
    const parent = options.parent ?? document.body;

    this.element = document.createElement('div');
    this.element.className = 'mandalizm-create';
    this.element.hidden = true;

    const inner = document.createElement('div');
    inner.className = 'mandalizm-create__inner';

    const title = document.createElement('div');
    title.className = 'mandalizm-create__title';
    title.textContent = 'CREATE SONG';

    this.audioInput = fileInput(AUDIO_FILE_ACCEPT);
    this.audioChosen = document.createElement('span');
    this.audioChosen.className = 'mandalizm-create__chosen';
    this.audioChosen.textContent = 'No audio selected';
    const audioField = this.buildPicker(
      'AUDIO', 'SELECT AUDIO', this.audioInput, this.audioChosen,
      () => this.onAudioChosen(),
    );

    this.titleInput = textInput('Song title');
    this.artistInput = textInput('Author');
    this.artistInput.value = options.defaultArtist ?? 'Jinn Project';

    this.coverInput = fileInput(COVER_FILE_ACCEPT);
    this.coverChosen = document.createElement('span');
    this.coverChosen.className = 'mandalizm-create__chosen';
    this.coverChosen.textContent = 'NO COVER';
    const coverField = this.buildPicker(
      'COVER IMAGE (OPTIONAL)', 'COVER IMAGE', this.coverInput, this.coverChosen,
      () => this.onCoverChosen(),
    );

    this.videoInput = fileInput(VIDEO_FILE_ACCEPT);
    this.videoChosen = document.createElement('span');
    this.videoChosen.className = 'mandalizm-create__chosen';
    this.videoChosen.textContent = 'NO MV';
    const videoField = this.buildPicker(
      'MV (OPTIONAL)', 'SELECT MV', this.videoInput, this.videoChosen,
      () => this.onVideoChosen(),
    );

    // AUTO first, because AUTO is what this screen does. CUSTOM is a way of
    // correcting the result, not a second workflow to choose between.
    const modeRow = document.createElement('div');
    modeRow.className = 'mandalizm-create__modes';
    // Three modes, and the order is the story: the engine decides, the
    // engine decides and shows its reasoning per section, or the player
    // decides. AUTO is untouched by the other two existing.
    this.modeButtons = (['AUTO', 'DIRECTED_AUTO', 'CUSTOM'] as const).map((value) => {
      const button = document.createElement('button');
      button.className = 'mandalizm-create__mode';
      button.textContent = value === 'DIRECTED_AUTO' ? 'DIRECTED AUTO' : value;
      button.dataset['mode'] = value;
      button.addEventListener('click', () => this.setMode(value));
      return button;
    });
    modeRow.append(...this.modeButtons);

    this.analysisSection = document.createElement('div');
    this.analysisSection.className = 'mandalizm-create__field';
    this.analysisSection.hidden = true;
    const analysisLabel = document.createElement('span');
    analysisLabel.className = 'mandalizm-create__label';
    analysisLabel.textContent = 'AUTO ANALYSIS';
    this.analysis = document.createElement('div');
    this.analysis.className = 'mandalizm-create__analysis';
    const legend = document.createElement('div');
    legend.className = 'mandalizm-create__legend';
    // The distinction the whole panel exists to make: two of these numbers
    // came from the song, the rest came from a config file.
    legend.innerHTML =
      '<span class="mandalizm-create__tag mandalizm-create__tag--measured">MEASURED</span>' +
      '<span class="mandalizm-create__tag">ENGINE DEFAULT</span>' +
      '<span class="mandalizm-create__tag mandalizm-create__tag--adjusted">YOURS</span>';
    this.analysisSection.append(analysisLabel, this.analysis, legend);

    // What the engine heard, shown for every generation including AUTO. A
    // player who cannot see where the engine thinks the song changes cannot
    // disagree with it, and disagreeing is the whole point of the panel
    // below.
    this.detectedSection = document.createElement('div');
    this.detectedSection.className = 'mandalizm-create__field';
    this.detectedSection.hidden = true;
    const detectedLabel = document.createElement('span');
    detectedLabel.className = 'mandalizm-create__label';
    detectedLabel.textContent = 'DETECTED SECTIONS';
    this.detectedList = document.createElement('div');
    this.detectedList.className = 'mandalizm-create__detected';
    this.detectedSection.append(detectedLabel, this.detectedList);

    this.advanced = document.createElement('details');
    this.advanced.className = 'mandalizm-create__advanced';
    const advancedSummary = document.createElement('summary');
    advancedSummary.textContent = 'ADJUST';
    this.advanced.appendChild(advancedSummary);
    for (const row of TUNING_ROWS) {
      this.advanced.appendChild(this.buildSlider(row));
    }
    const resetAll = document.createElement('button');
    resetAll.className = 'mandalizm-create__reset';
    resetAll.textContent = 'RESET ALL TO AUTO';
    resetAll.addEventListener('click', () => this.resetTuning());
    this.advanced.appendChild(resetAll);
    this.advanced.hidden = true;

    // Direction lives beside the dials rather than replacing them: GLOBAL
    // FEEL is how busy the chart is, this is what it is made of and what it
    // looks like. Both are corrections of an automatic result; neither is an
    // editor, and nothing here addresses a single note.
    this.directionPanel = document.createElement('details');
    this.directionPanel.className = 'mandalizm-create__advanced';
    const directionSummary = document.createElement('summary');
    directionSummary.textContent = 'DIRECT';
    this.directionPanel.appendChild(directionSummary);

    this.directionPanel.appendChild(this.buildWeightGroup('MUSIC DIRECTION', 'source', SOURCE_NAMES));
    this.directionPanel.appendChild(this.buildWeightGroup('GEOMETRY', 'geometry', GEOMETRY_NAMES));
    this.directionPanel.appendChild(this.buildWeightGroup('PATTERN', 'pattern', PATTERN_NAMES));

    const chunkRow = document.createElement('div');
    chunkRow.className = 'mandalizm-create__slider';
    const chunkLabel = document.createElement('span');
    chunkLabel.className = 'mandalizm-create__label';
    chunkLabel.textContent = 'PATTERN CHUNK';
    const chunkButtons = document.createElement('div');
    chunkButtons.className = 'mandalizm-create__modes';
    this.chunkButtons = CHUNK_SIZES.map((size) => {
      const button = document.createElement('button');
      button.className = 'mandalizm-create__mode';
      button.textContent = String(size);
      button.dataset['chunk'] = String(size);
      button.addEventListener('click', () => {
        this.direction = { ...this.direction, chunkSize: size as ChunkSize };
        this.mode = 'CUSTOM';
        this.syncDirection();
      });
      return button;
    });
    chunkButtons.append(...this.chunkButtons);
    chunkRow.append(chunkLabel, chunkButtons);
    this.directionPanel.appendChild(chunkRow);

    // One switch, because there are two honest ways for DIRECTED AUTO to work
    // and a player should be able to hear the difference rather than take it
    // on trust. On, the engine reads the song's structure — which parts come
    // back, what each one probably is — and directs from that. Off, it directs
    // from intensity alone, which is what it did before it could read anything
    // and is never wrong about what it claims.
    const structureRow = document.createElement('div');
    structureRow.className = 'mandalizm-create__slider';
    const structureLabel = document.createElement('span');
    structureLabel.className = 'mandalizm-create__label';
    structureLabel.textContent = 'READ STRUCTURE';
    this.structureToggle = document.createElement('input');
    this.structureToggle.type = 'checkbox';
    this.structureToggle.checked = true;
    this.structureToggle.dataset['role'] = 'read-structure';
    const structureHint = document.createElement('span');
    structureHint.className = 'mandalizm-create__hint';
    structureHint.textContent = 'DIRECTED AUTO reads sections, repeats and phrases. Off: intensity only.';
    structureRow.append(structureLabel, this.structureToggle, structureHint);
    this.directionPanel.appendChild(structureRow);

    // The timeline: a bar showing where the sections are, and a row per
    // section for what each one asks for. Deliberately not a DAW — there is
    // nothing to drag, because there are no notes to drag.
    const timelineLabel = document.createElement('span');
    timelineLabel.className = 'mandalizm-create__label';
    timelineLabel.textContent = 'TIMELINE DIRECTION';
    this.sectionBar = document.createElement('div');
    this.sectionBar.className = 'mandalizm-create__timeline';
    this.sectionList = document.createElement('div');
    this.sectionList.className = 'mandalizm-create__sections';
    const addSection = document.createElement('button');
    addSection.className = 'mandalizm-create__reset';
    addSection.textContent = 'ADD SECTION';
    addSection.dataset['role'] = 'add-section';
    addSection.addEventListener('click', () => this.addSection());
    this.directionPanel.append(timelineLabel, this.sectionBar, this.sectionList, addSection);
    this.directionPanel.hidden = true;

    this.feedbackSection = document.createElement('div');
    this.feedbackSection.className = 'mandalizm-create__field';
    this.feedbackSection.hidden = true;
    const feedbackLabel = document.createElement('span');
    feedbackLabel.className = 'mandalizm-create__label';
    feedbackLabel.textContent = 'HOW DOES IT FEEL?';
    const feedbackRow = document.createElement('div');
    feedbackRow.className = 'mandalizm-create__modes';
    // Three answers a player has without opening anything: the correction
    // is arithmetic on the dials they never had to look at.
    for (const [label, key] of [
      ['TOO BUSY', 'busy'],
      ['GOOD', 'good'],
      ['TOO EMPTY', 'empty'],
    ] as const) {
      const button = document.createElement('button');
      button.className = 'mandalizm-create__mode';
      button.textContent = label;
      button.dataset['feel'] = key;
      button.addEventListener('click', () => this.applyFeel(key));
      feedbackRow.appendChild(button);
    }
    const regenerate = document.createElement('button');
    regenerate.className = 'mandalizm-create__pick';
    regenerate.textContent = 'REGENERATE';
    regenerate.dataset['role'] = 'regenerate';
    regenerate.addEventListener('click', () => {
      void (this.options.onRegenerate ?? this.options.onGenerate)(this.fields());
    });
    this.feedbackSection.append(feedbackLabel, feedbackRow, regenerate);

    const actions = document.createElement('div');
    actions.className = 'mandalizm-create__actions';
    this.generateButton = action('GENERATE');
    this.generateButton.addEventListener('click', () => {
      void this.options.onGenerate(this.fields());
    });
    this.cancelButton = action('CANCEL', true);
    this.cancelButton.hidden = true;
    this.cancelButton.addEventListener('click', () => this.options.onCancel());
    this.closeButton = action('BACK', true);
    this.closeButton.addEventListener('click', () => this.options.onClose());
    actions.append(this.generateButton, this.cancelButton, this.closeButton);

    const bar = document.createElement('div');
    bar.className = 'mandalizm-create__bar';
    this.fill = document.createElement('div');
    this.fill.className = 'mandalizm-create__fill';
    bar.appendChild(this.fill);

    const statusLabel = document.createElement('span');
    statusLabel.className = 'mandalizm-create__label';
    statusLabel.textContent = 'STATUS';

    this.status = document.createElement('div');
    this.status.className = 'mandalizm-create__status';
    this.status.setAttribute('role', 'log');
    this.status.setAttribute('aria-live', 'polite');

    inner.append(
      title,
      modeRow,
      audioField,
      field('TITLE', this.titleInput),
      field('AUTHOR', this.artistInput),
      coverField,
      videoField,
      this.analysisSection,
      this.detectedSection,
      this.advanced,
      this.directionPanel,
      actions,
      this.feedbackSection,
      statusLabel,
      bar,
      this.status,
    );
    this.element.appendChild(inner);
    this.advancedSettings = new ResponsiveSettings(
      [coverField, videoField, this.analysisSection, this.detectedSection, this.advanced, this.directionPanel], inner,
      undefined, options.onTitle,
    );
    actions.before(this.advancedSettings.button);
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  setOpen(open: boolean): void {
    if (!open) this.advancedSettings.close();
    this.element.hidden = !open;
    if (open) this.titleInput.focus();
  }

  fields(): CreateSongFields {
    return {
      audio: this.audioInput.files?.[0] ?? null,
      cover: this.coverInput.files?.[0] ?? null,
      video: this.videoInput.files?.[0] ?? null,
      title: this.titleInput.value,
      artist: this.artistInput.value,
      mode: this.mode,
      tuning: { ...this.tuning },
      direction: this.direction,
      // Carried so a partial regeneration can ask for exactly what the pack
      // was built with; without it "regenerate this section" has nothing to
      // hold the other sections still against.
      lastDirection: this.lastSent,
      semantic: this.structureToggle.checked,
    };
  }

  /** The dials as they stand. */
  get currentTuning(): ChartTuning {
    return { ...this.tuning };
  }

  get currentMode(): GeneratorMode {
    return this.mode;
  }

  /**
   * Shows what the engine measured, and what it merely assumed.
   *
   * Called after every generation, AUTO included. An automatic chart the
   * player cannot see the reasoning behind is the thing they complain
   * about, and the split between the two columns is the honest part: BPM
   * was heard, density 1.00 was not.
   */
  /**
   * Shows the sections the engine found, and offers each for regeneration.
   *
   * The rows are measurements — where, how long, how intense, how sharply it
   * changed — and one button. Adopting them copies the spans into the
   * direction panel, where they stop being measurements and become the
   * player's to edit.
   */
  showDetectedSections(
    analysis: SectionAnalysis | null,
    directed: readonly Record<string, unknown>[],
    semantics: SemanticAnalysis | null = null,
  ): void {
    this.detected = analysis;
    this.semantics = semantics;
    this.detectedList.replaceChildren();
    if (analysis === null || analysis.sections.length === 0) {
      this.detectedSection.hidden = true;
      return;
    }
    this.detectedSection.hidden = false;
    const byIndex = new Map<number, Record<string, unknown>>();
    for (const row of directed) {
      const index = row['index'];
      if (typeof index === 'number') byIndex.set(index, row);
    }
    for (const section of analysis.sections) {
      this.detectedList.appendChild(this.buildDetectedRow(section, byIndex.get(section.index)));
    }
    const adopt = document.createElement('button');
    adopt.className = 'mandalizm-create__reset';
    adopt.textContent = 'USE THESE AS SECTIONS';
    adopt.dataset['role'] = 'adopt-sections';
    adopt.addEventListener('click', () => {
      const source = this.detected ?? analysis;
      this.direction = { ...this.direction, sections: sectionsFromAnalysis(source) };
      this.mode = 'CUSTOM';
      this.syncControls();
      this.syncDirection();
    });
    this.detectedList.appendChild(adopt);
  }

  /**
   * Takes on the direction the engine used, so the panel shows it.
   *
   * After DIRECTED AUTO the interesting direction is the engine's, not the
   * empty one the player never filled in. Adopting it makes the sections
   * editable and gives a later section regeneration the right baseline.
   */
  adoptDirection(direction: ChartDirection | null): void {
    if (direction === null) return;
    this.direction = direction;
    this.syncControls();
    this.syncDirection();
  }

  /** The direction the last generation actually used, for a partial redo. */
  setLastDirection(direction: ChartDirection | null): void {
    this.lastSent = direction;
  }

  /** How long the song is, so the timeline can be drawn to scale. */
  setSongDuration(seconds: number): void {
    this.songSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
    this.syncDirection();
  }

  showAnalysis(auto: AutoParameters | null, sources: EventSources | null): void {
    this.auto = auto;
    this.analysis.replaceChildren();
    if (auto === null) {
      this.analysisSection.hidden = true;
      return;
    }
    this.analysisSection.hidden = false;
    const measured = auto.measured;
    this.analysis.append(
      readout('BPM', measured.detectedBpm.toFixed(1), true),
      readout('FIRST BEAT', `${measured.firstBeatOffsetSeconds.toFixed(3)} s`, true),
      readout('BEAT GRID', `${measured.beatCount} @ ${Math.round(measured.beatIntervalSeconds * 1000)} ms`, true),
      readout('ONSETS FOUND', String(measured.onsetCount), true),
    );
    if (sources !== null) {
      const normal = sources['normal'] ?? {};
      const beat = normal['beat'] ?? 0;
      const melody = normal['melody'] ?? 0;
      const onset = normal['ornament'] ?? 0;
      this.analysis.append(
        readout('NOTE SOURCES', `${beat} beat / ${melody} melody / ${onset} onset`, true),
      );
    }
    if (directsAnything(this.direction)) {
      const shapes = Object.entries(this.direction.geometryWeights)
        .filter(([, weight]) => weight > 0)
        .map(([name, weight]) => `${name} ${weight}`)
        .join(' · ');
      if (shapes !== '') this.analysis.append(readout('GEOMETRY MIX', shapes, false, 'adjusted'));
      if (this.direction.sections.length > 0) {
        this.analysis.append(
          readout('SECTIONS', `${this.direction.sections.length} directed`, false, 'adjusted'),
        );
      }
    }
    for (const row of TUNING_ROWS) {
      const moved = Math.abs(this.tuning[row.key] - NEUTRAL_TUNING[row.key]) > 1e-9;
      this.analysis.append(
        readout(
          row.label,
          row.format(this.tuning[row.key], auto),
          false,
          moved ? 'adjusted' : 'default',
        ),
      );
    }
    this.syncControls();
  }

  /** Offers the three plain answers once a chart exists. */
  showFeedback(show: boolean): void {
    this.feedbackSection.hidden = !show;
  }

  /** Puts every dial back where the engine had it. */
  resetTuning(): void {
    this.tuning = { ...NEUTRAL_TUNING };
    this.direction = EMPTY_DIRECTION;
    for (const [key, input] of this.weightInputs) {
      input.value = key.startsWith('source:') ? '100' : '0';
      const value = input.nextElementSibling;
      if (value !== null) value.textContent = key.startsWith('source:') ? '100%' : '0';
    }
    this.syncControls();
    this.syncDirection();
  }

  /**
   * Locks the form for the duration of a generation.
   *
   * Every control, not just GENERATE: swapping the audio file halfway through
   * would leave the fields describing one song and the engine building another.
   */
  setBusy(busy: boolean): void {
    this.generateButton.disabled = busy;
    this.generateButton.textContent = busy ? 'GENERATING...' : 'GENERATE';
    this.cancelButton.hidden = !busy;
    this.closeButton.disabled = busy;
    this.titleInput.disabled = busy;
    this.artistInput.disabled = busy;
    for (const button of this.pickButtons) button.disabled = busy;
  }

  setPhase(phase: CreationPhase): void {
    this.setBusy(phase === 'preparing' || phase === 'generating' || phase === 'verifying');
  }

  /** Clears the log and the bar, for a fresh attempt. */
  resetStatus(): void {
    this.status.replaceChildren();
    this.stageLines.clear();
    this.setFraction(0);
  }

  /**
   * Adds or updates one stage line.
   *
   * Keyed by stage name: the engine reports the same stage repeatedly while it
   * works, and each should be one line that changes, not a hundred identical
   * ones.
   */
  setStage(stage: string, message: string, fraction: number): void {
    const existing = this.stageLines.get(stage);
    const line = existing ?? document.createElement('div');
    line.className = 'mandalizm-create__line mandalizm-create__line--active';
    line.textContent = message;
    if (existing === undefined) {
      this.stageLines.set(stage, line);
      // The previous stage stops being the active one the moment a new one
      // arrives, which is what makes the last line meaningful on a failure.
      for (const [key, other] of this.stageLines) {
        if (key !== stage) other.className = 'mandalizm-create__line';
      }
      this.append(line);
    }
    this.setFraction(fraction);
  }

  /** A free-text observation. Never overwritten, so the record is complete. */
  addNote(note: string): void {
    this.append(line('mandalizm-create__line--note', note));
  }

  addResult(text: string): void {
    this.append(line('mandalizm-create__line--done', text));
  }

  addError(text: string): void {
    this.append(line('mandalizm-create__line--error', text));
    this.setFraction(0);
  }

  dispose(): void {
    this.advancedSettings.dispose();
    this.element.remove();
  }

  private setMode(mode: GeneratorMode): void {
    this.mode = mode;
    // CUSTOM never starts from zero: the dials keep whatever AUTO reported,
    // so switching modes offers a correction rather than a blank form.
    this.advanced.hidden = mode !== 'CUSTOM';
    this.directionPanel.hidden = mode !== 'CUSTOM';
    if (mode === 'CUSTOM') this.advanced.open = true;
    this.syncControls();
  }

  private applyFeel(feedback: "busy" | "good" | "empty"): void {
    this.tuning = applyFeedback(this.tuning, feedback);
    if (feedback !== 'good') this.mode = 'CUSTOM';
    this.syncControls();
  }

  private buildSlider(row: (typeof TUNING_ROWS)[number]): HTMLDivElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'mandalizm-create__slider';
    const label = document.createElement('span');
    label.className = 'mandalizm-create__label';
    label.textContent = row.label;
    const value = document.createElement('span');
    value.className = 'mandalizm-create__slider-value';
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(row.min);
    input.max = String(row.max);
    input.step = String(row.step);
    input.dataset['tuning'] = row.key;
    input.title = row.hint;
    input.addEventListener('input', () => {
      this.tuning = clampTuning({ ...this.tuning, [row.key]: Number(input.value) });
      this.mode = 'CUSTOM';
      this.syncControls();
    });
    const reset = document.createElement('button');
    reset.className = 'mandalizm-create__reset';
    reset.textContent = 'RESET AUTO';
    reset.dataset['reset'] = row.key;
    reset.addEventListener('click', () => {
      this.tuning = { ...this.tuning, [row.key]: NEUTRAL_TUNING[row.key] };
      this.syncControls();
    });
    this.sliders.set(row.key, input);
    this.sliderValues.set(row.key, value);
    wrapper.append(label, input, value, reset);
    return wrapper;
  }

  /** One place where every control is redrawn from the current state. */
  private syncControls(): void {
    for (const button of this.modeButtons) {
      const active = button.dataset['mode'] === this.mode;
      button.classList.toggle('mandalizm-create__mode--active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    this.advanced.hidden = this.mode !== 'CUSTOM';
    this.directionPanel.hidden = this.mode !== 'CUSTOM';
    for (const row of TUNING_ROWS) {
      const input = this.sliders.get(row.key);
      const value = this.sliderValues.get(row.key);
      if (input !== undefined) input.value = String(this.tuning[row.key]);
      if (value !== undefined) value.textContent = row.format(this.tuning[row.key], this.auto);
    }
    for (const node of this.analysis.querySelectorAll<HTMLElement>("[data-auto-row]")) {
      const key = node.dataset['autoRow'];
      const row = TUNING_ROWS.find((candidate) => candidate.label === key);
      if (row === undefined) continue;
      const slot = node.querySelector('.mandalizm-create__readout-value');
      if (slot !== null) slot.textContent = row.format(this.tuning[row.key], this.auto);
      const moved = Math.abs(this.tuning[row.key] - NEUTRAL_TUNING[row.key]) > 1e-9;
      const tag = node.querySelector('.mandalizm-create__tag');
      if (tag !== null) {
        tag.className = moved
          ? 'mandalizm-create__tag mandalizm-create__tag--adjusted'
          : 'mandalizm-create__tag';
        tag.textContent = moved ? 'YOURS' : 'ENGINE DEFAULT';
      }
    }
  }

  /**
   * One group of weights: a row per name, each a 0..100 slider.
   *
   * Percentages are not required to add up. They are weights, normalised
   * where they are used, so a player can say "mostly circles, some spirals"
   * without doing arithmetic to make it total a hundred.
   */
  private buildWeightGroup(
    title: string,
    kind: 'source' | 'geometry' | 'pattern',
    names: readonly string[],
  ): HTMLDivElement {
    const group = document.createElement('div');
    group.className = 'mandalizm-create__group';
    const label = document.createElement('span');
    label.className = 'mandalizm-create__label';
    label.textContent = title;
    group.appendChild(label);
    for (const name of names) {
      const row = document.createElement('div');
      row.className = 'mandalizm-create__slider';
      const rowLabel = document.createElement('span');
      rowLabel.className = 'mandalizm-create__label';
      rowLabel.textContent = name.replace(/_/g, ' ');
      const input = document.createElement('input');
      input.type = 'range';
      input.min = '0';
      input.max = '100';
      input.step = '5';
      input.dataset['weight'] = `${kind}:${name}`;
      // Sources start at "take everything", shapes and traversals at zero:
      // one is a bias on what already happens, the other is a request for
      // something that otherwise would not.
      input.value = kind === 'source' ? '100' : '0';
      const value = document.createElement('span');
      value.className = 'mandalizm-create__slider-value';
      value.textContent = kind === 'source' ? '100%' : '0';
      input.addEventListener('input', () => {
        this.setWeight(kind, name, Number(input.value));
        value.textContent = kind === 'source' ? `${input.value}%` : input.value;
      });
      this.weightInputs.set(`${kind}:${name}`, input);
      row.append(rowLabel, input, value);
      group.appendChild(row);
    }
    return group;
  }

  private setWeight(kind: 'source' | 'geometry' | 'pattern', name: string, value: number): void {
    const key = kind === 'source' ? 'sourceWeights' : kind === 'geometry' ? 'geometryWeights' : 'patternWeights';
    const weights: Record<string, number> = { ...this.direction[key] };
    // A source weight is a fraction of what would otherwise be taken; a
    // geometry or pattern weight is a share of the draw.
    weights[name] = kind === 'source' ? value / 100 : value;
    this.direction = { ...this.direction, [key]: weights };
    this.mode = 'CUSTOM';
    this.syncDirection();
  }

  /** A new section over the part of the song nothing has claimed yet. */
  private addSection(): void {
    const duration = this.songSeconds > 0 ? this.songSeconds : 180;
    const last = this.direction.sections[this.direction.sections.length - 1];
    const start = last === undefined ? 0 : Math.min(last.endTime, duration - 1);
    const end = Math.min(duration, start + Math.max(8, duration / 6));
    if (end <= start) return;
    const sections = tidySections([
      ...this.direction.sections,
      emptySection(start, end, `part ${this.direction.sections.length + 1}`),
    ]);
    this.direction = { ...this.direction, sections };
    this.mode = 'CUSTOM';
    this.syncDirection();
  }

  private updateSection(index: number, patch: Partial<DirectionSection>): void {
    const sections = this.direction.sections.map((section, position) =>
      position === index ? { ...section, ...patch } : section,
    );
    this.direction = { ...this.direction, sections: tidySections(sections) };
    this.syncDirection();
  }

  private removeSection(index: number): void {
    const sections = this.direction.sections.filter((_, position) => position !== index);
    this.direction = { ...this.direction, sections };
    this.syncDirection();
  }

  /** Redraws the timeline and the section rows from the current direction. */
  private syncDirection(): void {
    for (const button of this.chunkButtons) {
      const active = Number(button.dataset['chunk']) === this.direction.chunkSize;
      button.classList.toggle('mandalizm-create__mode--active', active);
    }
    const duration = this.songSeconds > 0 ? this.songSeconds : 180;
    this.sectionBar.replaceChildren();
    for (const section of this.direction.sections) {
      const block = document.createElement('div');
      block.className = 'mandalizm-create__timeline-block';
      block.style.left = `${(section.startTime / duration) * 100}%`;
      block.style.width = `${((section.endTime - section.startTime) / duration) * 100}%`;
      block.title = `${section.label} ${section.startTime.toFixed(1)}–${section.endTime.toFixed(1)}s`;
      this.sectionBar.appendChild(block);
    }

    this.sectionList.replaceChildren();
    this.direction.sections.forEach((section, index) => {
      const row = document.createElement('div');
      row.className = 'mandalizm-create__section';
      row.dataset['section'] = String(index);

      const start = numberInput(section.startTime, (value) =>
        this.updateSection(index, { startTime: Math.max(0, value) }),
      );
      start.dataset['sectionStart'] = String(index);
      const end = numberInput(section.endTime, (value) =>
        this.updateSection(index, { endTime: Math.max(0.5, value) }),
      );
      end.dataset['sectionEnd'] = String(index);

      // One shape and one traversal per section, chosen rather than
      // weighted: a region small enough to point at is a region small
      // enough to want one answer for.
      const shape = select(['—', ...GEOMETRY_NAMES], (value) =>
        this.updateSection(index, { geometryWeights: value === '—' ? {} : { [value]: 100 } }),
      );
      shape.dataset['sectionGeometry'] = String(index);
      shape.value = Object.keys(section.geometryWeights)[0] ?? '—';
      const pattern = select(['—', ...PATTERN_NAMES], (value) =>
        this.updateSection(index, { patternWeights: value === '—' ? {} : { [value]: 100 } }),
      );
      pattern.dataset['sectionPattern'] = String(index);
      pattern.value = Object.keys(section.patternWeights)[0] ?? '—';

      const remove = document.createElement('button');
      remove.className = 'mandalizm-create__reset';
      remove.textContent = 'REMOVE';
      remove.addEventListener('click', () => this.removeSection(index));

      const name = document.createElement('span');
      name.className = 'mandalizm-create__label';
      name.textContent = section.label || `part ${index + 1}`;
      row.append(name, start, end, shape, pattern, remove);
      this.sectionList.appendChild(row);
    });
  }

  private buildDetectedRow(
    section: DetectedSection,
    directed: Record<string, unknown> | undefined,
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'mandalizm-create__detected-row';
    row.dataset['detected'] = String(section.index);

    const label = document.createElement('span');
    label.className = 'mandalizm-create__tag mandalizm-create__tag--measured';
    label.textContent = section.label;

    const span = document.createElement('span');
    span.textContent = `${section.startTime.toFixed(1)}–${section.endTime.toFixed(1)}s`;

    const detail = document.createElement('span');
    detail.className = 'mandalizm-create__detected-detail';
    detail.textContent =
      `${section.beats} beats · energy ${section.energyMean.toFixed(2)} · ` +
      `onsets ${section.onsetDensity.toFixed(2)} · change ${section.changeConfidence.toFixed(2)}`;

    // What the engine *thinks* this section is, kept visibly apart from what it
    // measured. A role is only ever shown with its confidence beside it and a
    // question mark on it, because it is a guess; a role it could not make is
    // shown as UNKNOWN rather than hidden, since "I don't know" is the answer
    // and hiding it would read as "nothing to say here".
    const reading = this.semantics?.sections.find((item) => item.index === section.index);
    const inferred = document.createElement('span');
    inferred.className = 'mandalizm-create__detected-detail';
    inferred.dataset['role'] = 'semantic-reading';
    if (reading !== undefined) {
      const role =
        reading.semanticRole === 'UNKNOWN'
          ? 'UNKNOWN'
          : `${reading.semanticRole}? ${reading.semanticRoleConfidence.toFixed(2)}`;
      const transient = reading.transientProfile;
      const parts = [
        role,
        `Melody ${reading.melodicActivity.toFixed(2)}`,
        `Harmony ${String.fromCharCode(0x394)} ${reading.harmonicActivity.toFixed(2)}`,
      ];
      if (transient !== null && transient.count > 0) {
        parts.push(
          `Transient ${shortTransient(transient.dominant)} ${transient.confidence.toFixed(2)}`,
        );
      }
      // Only worth showing when it means something: a group of one is not a
      // recurrence, it is a section that happens to have an index.
      if (reading.similarToSections.length > 0) {
        parts.push(
          `Recurrence #${reading.recurrenceGroup} ` +
            `(${reading.recurrenceStrength.toFixed(2)}, with ${reading.similarToSections.join(', ')})`,
        );
      }
      inferred.textContent = parts.join(' · ');
      inferred.dataset['semanticRole'] = reading.semanticRole;
      inferred.dataset['semanticConfidence'] = reading.semanticRoleConfidence.toFixed(4);
      inferred.dataset['recurrenceGroup'] = String(reading.recurrenceGroup);
    }

    const decided = document.createElement('span');
    decided.className = 'mandalizm-create__detected-detail';
    decided.dataset['role'] = 'directed';
    if (directed !== undefined) {
      const geometry = Object.keys((directed['geometry'] ?? {}) as Record<string, number>)[0] ?? '—';
      const pattern = Object.keys((directed['pattern'] ?? {}) as Record<string, number>)[0] ?? '—';
      const density = typeof directed['density'] === 'number' ? directed['density'] : 1;
      decided.textContent = `→ ${geometry} · ${pattern} · density ${density.toFixed(2)}`;
    }

    const regenerate = document.createElement('button');
    regenerate.className = 'mandalizm-create__reset';
    regenerate.textContent = 'REGENERATE SECTION';
    regenerate.dataset['regenerateSection'] = String(section.index);
    regenerate.addEventListener('click', () => {
      void (this.options.onRegenerateSection ?? (() => undefined))(section.index, this.fields());
    });

    row.append(label, span, detail, inferred, decided, regenerate);
    return row;
  }

  private setFraction(fraction: number): void {
    const clamped = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
    this.fill.style.width = `${(clamped * 100).toFixed(1)}%`;
  }

  private append(node: HTMLElement): void {
    this.status.appendChild(node);
    this.status.scrollTop = this.status.scrollHeight;
  }

  private buildPicker(
    labelText: string,
    buttonText: string,
    input: HTMLInputElement,
    chosen: HTMLSpanElement,
    onChange: () => void,
  ): HTMLDivElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'mandalizm-create__field';
    const label = document.createElement('span');
    label.className = 'mandalizm-create__label';
    label.textContent = labelText;
    const row = document.createElement('div');
    row.className = 'mandalizm-create__row';
    const button = document.createElement('button');
    button.className = 'mandalizm-create__pick';
    button.type = 'button';
    button.textContent = buttonText;
    // The native control is hidden and clicked through this button: browsers
    // only open a file dialog from a real gesture, and this keeps that gesture
    // while letting the button look like everything else on the screen.
    button.addEventListener('click', () => input.click());
    input.addEventListener('change', onChange);
    this.pickButtons.push(button);
    row.append(button, chosen, input);
    wrapper.append(label, row);
    return wrapper;
  }

  private onAudioChosen(): void {
    const file = this.audioInput.files?.[0];
    if (file === undefined) {
      this.audioChosen.textContent = 'No audio selected';
      return;
    }
    this.audioChosen.textContent = `${file.name} — ${formatMib(file.size)}`;
    // A title is required and the file name is the best guess available. Only
    // filled in when the field is still empty, so it never overwrites a typed
    // one — and it is a suggestion, not a rule: `displayName` is what Mirror
    // Drift resolves the MV from, so the player may well want it different.
    if (this.titleInput.value.trim().length === 0) {
      this.titleInput.value = file.name.replace(/\.[^.]+$/, '');
    }
  }

  private onCoverChosen(): void {
    const file = this.coverInput.files?.[0];
    this.coverChosen.textContent =
      file === undefined ? 'NO COVER' : `${file.name} — ${formatMib(file.size)}`;
  }

  private onVideoChosen(): void {
    const file = this.videoInput.files?.[0];
    this.videoChosen.textContent =
      file === undefined ? 'NO MV' : `${file.name} — ${formatMib(file.size)}`;
  }
}

// ---------------------------------------------------------------------------

function field(labelText: string, input: HTMLInputElement): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'mandalizm-create__field';
  const label = document.createElement('label');
  label.className = 'mandalizm-create__label';
  label.textContent = labelText;
  label.htmlFor = input.id;
  wrapper.append(label, input);
  return wrapper;
}

let inputSequence = 0;

function textInput(placeholder: string): HTMLInputElement {
  const input = document.createElement('input');
  input.className = 'mandalizm-create__input';
  input.type = 'text';
  input.placeholder = placeholder;
  inputSequence += 1;
  input.id = `mandalizm-create-${inputSequence}`;
  return input;
}

/**
 * One line of the AUTO ANALYSIS panel.
 *
 * `measured` is not decoration. A player looking at this list has no way to
 * tell that the BPM was heard in their song while the density was read out of
 * a config file, and telling them the engine chose 1.00 for them would be a
 * lie the rest of the feature is built on.
 */
function readout(
  label: string,
  value: string,
  measured: boolean,
  origin: 'measured' | 'default' | 'adjusted' = measured ? 'measured' : 'default',
): HTMLDivElement {
  const row = document.createElement('div');
  row.className = measured
    ? 'mandalizm-create__readout mandalizm-create__readout--measured'
    : 'mandalizm-create__readout';
  row.dataset['autoRow'] = label;
  const name = document.createElement('span');
  name.textContent = label;
  const slot = document.createElement('span');
  slot.className = 'mandalizm-create__readout-value';
  slot.textContent = value;
  const tag = document.createElement('span');
  // Three states, because two would lie in one of them: a dial the player
  // moved is neither something the engine heard nor something it assumed.
  tag.className =
    origin === 'measured'
      ? 'mandalizm-create__tag mandalizm-create__tag--measured'
      : origin === 'adjusted'
        ? 'mandalizm-create__tag mandalizm-create__tag--adjusted'
        : 'mandalizm-create__tag';
  tag.textContent =
    origin === 'measured' ? 'MEASURED' : origin === 'adjusted' ? 'YOURS' : 'ENGINE DEFAULT';
  row.append(name, slot, tag);
  return row;
}

/** The transient kind, short enough for a row that already says a lot. */
function shortTransient(kind: string): string {
  if (kind === 'KICK_LIKE') return 'KICK';
  if (kind === 'SNARE_LIKE') return 'SNARE';
  if (kind === 'HIGH_PERCUSSION') return 'HIGH';
  return 'MIXED';
}

function numberInput(value: number, onChange: (value: number) => void): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.step = '0.5';
  input.min = '0';
  // One decimal to read, never to keep. The value shown is a rounding of the
  // section boundary; the boundary itself stays whatever the engine measured,
  // and is only replaced when someone actually edits the field. A round-trip
  // through this display would move an event across a section edge.
  input.value = value.toFixed(1);
  input.className = 'mandalizm-create__number';
  input.addEventListener('change', () => onChange(Number(input.value)));
  return input;
}

function select(options: readonly string[], onChange: (value: string) => void): HTMLSelectElement {
  const element = document.createElement('select');
  element.className = 'mandalizm-create__select';
  for (const option of options) {
    const item = document.createElement('option');
    item.value = option;
    item.textContent = option;
    element.appendChild(item);
  }
  element.addEventListener('change', () => onChange(element.value));
  return element;
}

function fileInput(accept: string): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.hidden = true;
  return input;
}

function action(text: string, quiet = false): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = quiet
    ? 'mandalizm-create__action mandalizm-create__action--quiet'
    : 'mandalizm-create__action';
  button.type = 'button';
  button.textContent = text;
  return button;
}

function line(modifier: string, text: string): HTMLDivElement {
  const element = document.createElement('div');
  element.className = `mandalizm-create__line ${modifier}`;
  element.textContent = text;
  return element;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
